import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { brand, nav } from "../data/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const isHome = location.pathname === "/";
  // Transparent (over the dark home hero) only at the top of the home page.
  const solid = scrolled || open || !isHome;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        solid
          ? "bg-cream/95 py-3 shadow-[0_1px_20px_rgba(30,23,16,0.08)] backdrop-blur-sm"
          : "bg-transparent py-5"
      } ${solid ? "border-b border-gold/20" : "border-b border-transparent"}`}
    >
      <nav className="container-luxe flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to="/" className="flex shrink-0 items-center" aria-label={brand.name}>
          <img
            src={solid ? brand.logoBlack : brand.logoWhite}
            alt={brand.name}
            className={`w-auto transition-all duration-500 ${
              solid ? "h-9 sm:h-10" : "h-10 sm:h-12"
            }`}
          />
        </Link>

        {/* Desktop nav */}
        <ul className="hidden items-center gap-9 lg:flex">
          {nav.map((item) => (
            <li key={item.to}>
              <NavItem {...item} solid={solid} />
            </li>
          ))}
          <li>
            <Link
              to="/contact"
              className={`border px-6 py-2.5 font-sans text-[11px] uppercase tracking-[0.25em] transition-all duration-500 ${
                solid
                  ? "border-gold text-gold-dark hover:bg-gold hover:text-white"
                  : "border-cream/70 text-cream hover:bg-cream hover:text-charcoal"
              }`}
            >
              Enquire
            </Link>
          </li>
        </ul>

        {/* Mobile toggle */}
        <button
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="flex h-10 w-10 flex-col items-center justify-center gap-1.5 lg:hidden"
        >
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className={`h-px w-6 transition-all duration-300 ${
                solid ? "bg-charcoal" : "bg-cream"
              } ${
                open && i === 0
                  ? "translate-y-[7px] rotate-45"
                  : open && i === 2
                  ? "-translate-y-[7px] -rotate-45"
                  : open && i === 1
                  ? "opacity-0"
                  : ""
              }`}
            />
          ))}
        </button>
      </nav>

      {/* Mobile menu */}
      <div
        className={`overflow-hidden transition-all duration-500 lg:hidden ${
          open ? "max-h-[26rem] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <ul className="container-luxe flex flex-col gap-1 pb-6 pt-4">
          {nav.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                className="block border-b border-gold/10 py-3 font-serif text-2xl text-charcoal"
              >
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    </header>
  );
}

function NavItem({ to, label, solid }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `group relative font-sans text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
          isActive
            ? "text-gold"
            : solid
            ? "text-charcoal/80 hover:text-gold-dark"
            : "text-cream/85 hover:text-white"
        }`
      }
    >
      {label}
      <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
    </NavLink>
  );
}
